import { supabase } from '../lib/supabase';
import { logger } from '../services/loggingService';
import { toast } from '../lib/toast';
import { AppError, DatabaseError } from '../lib/errors';

interface DatabaseOperation<T> {
  data?: T;
  error?: string;
  success: boolean;
}

/**
 * Standardized database operation wrapper with consistent error handling
 */
export async function dbOperation<T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  context: {
    operation: string;
    entity: string;
    userId?: string;
    projectId?: string;
    showUserError?: boolean;
  }
): Promise<DatabaseOperation<T>> {
  try {
    const startTime = Date.now();
    const result = await operation();
    const duration = Date.now() - startTime;

    // Log the operation
    logger.debug(`Database operation: ${context.operation}`, {
      entity: context.entity,
      userId: context.userId,
      projectId: context.projectId,
      duration,
      success: !result.error
    });

    if (result.error) {
      // Check for specific error types
      const errorMessage = result.error.message || result.error;
      
      // RLS policy violation
      if (errorMessage.includes('new row violates row-level security policy')) {
        logger.error('RLS policy violation', {
          ...context,
          error: errorMessage
        });
        
        if (context.showUserError !== false) {
          toast.error('You don\'t have permission to perform this action');
        }
        
        return {
          error: 'Permission denied',
          success: false
        };
      }

      // Foreign key violation
      if (errorMessage.includes('violates foreign key constraint')) {
        logger.error('Foreign key constraint violation', {
          ...context,
          error: errorMessage
        });
        
        if (context.showUserError !== false) {
          toast.error('Related data not found. Please refresh and try again.');
        }
        
        return {
          error: 'Related data not found',
          success: false
        };
      }

      // Unique constraint violation
      if (errorMessage.includes('duplicate key value')) {
        logger.error('Duplicate key violation', {
          ...context,
          error: errorMessage
        });
        
        if (context.showUserError !== false) {
          toast.error('This item already exists');
        }
        
        return {
          error: 'Duplicate entry',
          success: false
        };
      }

      // Generic database error
      logger.error(`Database operation failed: ${context.operation}`, {
        ...context,
        error: errorMessage
      });

      if (context.showUserError !== false) {
        toast.error('An error occurred. Please try again.');
      }

      return {
        error: errorMessage,
        success: false
      };
    }

    // Check if data is null when we expected something
    if (!result.data && context.operation.startsWith('get')) {
      logger.warn(`No data returned for ${context.operation}`, context);
      return {
        data: result.data as T,
        success: true
      };
    }

    return {
      data: result.data as T,
      success: true
    };
  } catch (error) {
    // Unexpected error
    logger.error(`Unexpected error in database operation: ${context.operation}`, {
      ...context,
      error
    });

    if (context.showUserError !== false) {
      toast.error('An unexpected error occurred');
    }

    return {
      error: error instanceof Error ? error.message : 'Unknown error',
      success: false
    };
  }
}

/**
 * Database transaction wrapper for atomic operations
 */
export async function dbTransaction<T>(
  operations: Array<() => Promise<{ data: any; error: any }>>,
  context: {
    transaction: string;
    userId?: string;
    projectId?: string;
  }
): Promise<DatabaseOperation<T[]>> {
  const results: T[] = [];
  const startTime = Date.now();

  try {
    logger.debug(`Starting transaction: ${context.transaction}`, context);

    for (let i = 0; i < operations.length; i++) {
      const result = await operations[i]();
      
      if (result.error) {
        // Rollback by not completing remaining operations
        logger.error(`Transaction failed at step ${i + 1}`, {
          ...context,
          step: i + 1,
          error: result.error
        });

        // Attempt rollback if needed
        // Note: Supabase doesn't have built-in transactions for client SDK
        // This is a best-effort approach
        
        toast.error('Operation failed. Changes have been rolled back.');
        
        return {
          error: `Transaction failed at step ${i + 1}: ${result.error.message}`,
          success: false
        };
      }

      results.push(result.data);
    }

    const duration = Date.now() - startTime;
    logger.info(`Transaction completed: ${context.transaction}`, {
      ...context,
      duration,
      steps: operations.length
    });

    return {
      data: results,
      success: true
    };
  } catch (error) {
    logger.error(`Transaction error: ${context.transaction}`, {
      ...context,
      error
    });

    toast.error('Transaction failed. Please try again.');

    return {
      error: error instanceof Error ? error.message : 'Transaction failed',
      success: false
    };
  }
}

/**
 * Check if user has permission for an operation
 */
export async function checkPermission(
  userId: string,
  resource: string,
  operation: 'read' | 'write' | 'delete',
  resourceId?: string
): Promise<boolean> {
  try {
    // Check user role first
    const { data: user } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single();

    if (!user) return false;

    // Super admins have all permissions
    if (user.role === 'super_admin') return true;

    // Check specific resource permissions
    switch (resource) {
      case 'project':
        if (!resourceId) return false;
        
        const { data: project } = await supabase
          .from('projects')
          .select('user_id, organization_id')
          .eq('id', resourceId)
          .single();

        if (!project) return false;

        // Owner has all permissions
        if (project.user_id === userId) return true;

        // Check organization membership
        if (project.organization_id) {
          const { data: member } = await supabase
            .from('organization_members')
            .select('role')
            .eq('organization_id', project.organization_id)
            .eq('user_id', userId)
            .single();

          if (!member) return false;

          // Check role-based permissions
          if (operation === 'read') return true; // All members can read
          if (operation === 'write' && ['admin', 'member'].includes(member.role)) return true;
          if (operation === 'delete' && member.role === 'admin') return true;
        }
        break;

      case 'organization':
        if (!resourceId) return false;
        
        const { data: member } = await supabase
          .from('organization_members')
          .select('role')
          .eq('organization_id', resourceId)
          .eq('user_id', userId)
          .single();

        if (!member) return false;

        if (operation === 'read') return true;
        if (operation === 'write' && ['admin', 'member'].includes(member.role)) return true;
        if (operation === 'delete' && member.role === 'admin') return true;
        break;
    }

    return false;
  } catch (error) {
    logger.error('Permission check failed', {
      userId,
      resource,
      operation,
      resourceId,
      error
    });
    return false;
  }
}

/**
 * Retry a database operation with exponential backoff
 */
export async function retryDbOperation<T>(
  operation: () => Promise<DatabaseOperation<T>>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<DatabaseOperation<T>> {
  let lastError: string | undefined;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const result = await operation();

    if (result.success) {
      return result;
    }

    lastError = result.error;

    // Don't retry on certain errors
    if (
      lastError?.includes('violates row-level security policy') ||
      lastError?.includes('duplicate key value') ||
      lastError?.includes('violates foreign key constraint')
    ) {
      return result;
    }

    // Wait before retrying
    if (attempt < maxRetries - 1) {
      const delay = baseDelay * Math.pow(2, attempt);
      logger.debug(`Retrying database operation (attempt ${attempt + 2}/${maxRetries})`, {
        delay,
        lastError
      });
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  return {
    error: lastError || 'Operation failed after retries',
    success: false
  };
}
